/**
 * The built-in automation library — a side panel of ready-made automations you
 * add in one click.
 *
 * Named "built-in" rather than "add automation" because there are now two ways
 * to create one and they are genuinely different things: describe what you want
 * to the assistant and it builds a bespoke automation, or pick one of these,
 * which we wrote and maintain. Calling both "add automation" made the second
 * indistinguishable from the first.
 *
 * Extracted from `automations-section.tsx` so the settings section and the
 * top-level automations page share one implementation. These templates are the
 * ONLY way to create lead rotation, CRM ingestion and date reminders — the
 * assistant cannot produce them — so the panel has to exist wherever
 * automations are managed.
 */

import { useQuery } from '@tanstack/react-query';
import { Calendar } from '@untitledui/icons';
import React, { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { cx } from '@/utils/cx';

import {
  type CrmWebhookSecretData,
  CrmWebhookSecretModal,
} from './CrmWebhookSecretModal';
import {
  AUTOMATION_TEMPLATES,
  type AutomationTemplate,
  filterAutomationTemplates,
} from './templates';
import { useBuiltInAutomationMutations } from './useBuiltInAutomationMutations';

const BOOK_TIME_URL = 'https://meet.ahoy.ai/ahoy-team';

/**
 * The only property this component needs — which script an automation runs, to
 * tell whether a template is already added. Narrow on purpose: a wider request
 * would fetch fields nothing here reads.
 */
const EXISTING_SCRIPT_PROPERTIES = ['ahoy_automation_name'];

interface TemplateButtonProps {
  template: AutomationTemplate;
  alreadyCreated: boolean;
  onSelectTemplate: (id: string) => void;
  close: () => void;
}

const TemplateButton = React.memo(function TemplateButton({
  template,
  alreadyCreated,
  onSelectTemplate,
  close,
}: TemplateButtonProps) {
  const handleClick = useCallback(() => {
    onSelectTemplate(template.id);
    close();
  }, [onSelectTemplate, template.id, close]);

  const Icon = template.icon;
  // Card-style Button (lib/base). `color="tertiary"` gives no pill chrome, and
  // `[&>[data-text]]:contents` flattens the Button's inner text wrapper so the
  // icon box and text block lay out as a two-column card.
  return (
    <Button
      color='tertiary'
      noTextPadding
      isDisabled={alreadyCreated}
      onClick={handleClick}
      className={cx(
        'flex w-full items-start justify-start gap-3 whitespace-normal rounded-lg border border-[var(--color-border-secondary)] p-4 text-left transition-colors [&>[data-text]]:contents',
        alreadyCreated
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-[var(--color-bg-secondary)]'
      )}
    >
      <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--color-bg-tertiary)]'>
        <Icon className='size-5 text-[var(--color-text-secondary)]' />
      </div>
      <div>
        <p className='text-sm font-medium text-[var(--color-text-primary)]'>
          {template.name}
        </p>
        <p className='mt-0.5 text-xs text-[var(--color-text-tertiary)]'>
          {alreadyCreated ? 'Already added' : template.description}
        </p>
      </div>
    </Button>
  );
});

export interface BuiltInAutomationLibraryProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number | undefined;
  /** Active feature flags, for the per-template `featureFlag` / `internal` gates. */
  activeFlags: readonly string[];
}

export function BuiltInAutomationLibrary({
  isOpen,
  onOpenChange,
  orgId,
  activeFlags,
}: BuiltInAutomationLibraryProps) {
  // Which templates are already in the org, resolved HERE rather than passed
  // in. Each host would otherwise have to supply it, and the automations page
  // could not: its list lives inside CRMObjectListPage. It shipped passing an
  // empty set, which left every template addable and one tap away from a
  // duplicate. Owning the lookup makes that impossible to get wrong.
  //
  // React Query dedupes by key, so sharing a host's list costs no extra fetch.
  const listQuery = useQuery({
    ...crmObjectListSentinelQuery(orgId ?? 0, 'ahoy_event_automation', {
      properties: EXISTING_SCRIPT_PROPERTIES,
    }),
    enabled: Boolean(orgId) && isOpen,
  });
  const { results } = useStoreBackedList(listQuery);

  const existingScriptNames = useMemo(() => {
    const names = new Set<string>();
    for (const automation of results) {
      const scriptName = automation.properties?.['ahoy_automation_name']?.value;
      if (typeof scriptName === 'string' && scriptName) {
        names.add(scriptName);
      }
    }
    return names;
  }, [results]);
  const [secretData, setSecretData] = useState<CrmWebhookSecretData | null>(
    null
  );

  const handleCrmWebhookSecret = useCallback((data: CrmWebhookSecretData) => {
    // Functional setter so a duplicate handler invocation cannot overwrite an
    // already-set token (StrictMode defense in depth).
    setSecretData(prev => prev ?? data);
  }, []);

  const { leadRotationMutation, crmWebhookMutation, reminderMutation } =
    useBuiltInAutomationMutations({
      orgId,
      onCrmWebhookSecret: handleCrmWebhookSecret,
    });

  const handleSelectTemplate = useCallback(
    (templateId: string) => {
      if (templateId === 'lead_rotation') {
        leadRotationMutation.mutate();
      } else if (templateId === 'crm_webhook') {
        // Guard against double-fire: each click creates a new webhook, so an
        // in-flight check prevents duplicates if the user re-opens the panel
        // and re-clicks before the first response.
        if (!crmWebhookMutation.isPending) {
          crmWebhookMutation.mutate();
        }
        // NB the template id, not the script name — they differ for this one
        // (`date_reminders` vs `reminder_from_date_properties`).
      } else if (templateId === 'date_reminders') {
        if (!reminderMutation.isPending) {
          reminderMutation.mutate();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
    [
      leadRotationMutation.mutate,
      crmWebhookMutation.mutate,
      crmWebhookMutation.isPending,
      reminderMutation.mutate,
      reminderMutation.isPending,
    ]
  );

  const visibleTemplates = useMemo(
    () => filterAutomationTemplates(AUTOMATION_TEMPLATES, { activeFlags }),
    [activeFlags]
  );

  const handleSecretModalClose = useCallback(() => setSecretData(null), []);
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <>
      {/*
        Composed with `SlideoutMenu.Header` / `.Content` rather than the
        `header` prop, matching every other CRM slideout — CreatePipelineSlideout,
        StagesSlideout, EditEntityTypeSlideout, PropertyTypeSlideout,
        CrmCreateForm. The `header` prop route is what the settings
        version used, and it renders a visibly different heading; the panel
        looked foreign next to the pipeline one because of it.
      */}
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable
        instance='built-in-automations'
        dialogAriaLabel='Built-in automations'
      >
        <SlideoutMenu.Header onClose={handleClose}>
          <h2 className='text-lg font-semibold text-primary'>
            Built-in automations
          </h2>
          <p className='text-sm text-tertiary mt-1'>
            Ready-made automations you can add in one click. For anything else,
            describe what you want and Ahoy will build it.
          </p>
        </SlideoutMenu.Header>

        <SlideoutMenu.Content>
          <div className='flex flex-col gap-3 py-4'>
            {visibleTemplates.map(template => (
              <TemplateButton
                key={template.id}
                template={template}
                alreadyCreated={template.scriptNames.some(s =>
                  existingScriptNames.has(s)
                )}
                onSelectTemplate={handleSelectTemplate}
                close={handleClose}
              />
            ))}
            <div className='mt-2 border-t border-[var(--color-border-secondary)] pt-4'>
              <Button
                href={BOOK_TIME_URL}
                target='_blank'
                rel='noopener noreferrer'
                color='tertiary'
                noTextPadding
                className='flex w-full items-start justify-start gap-3 whitespace-normal rounded-lg bg-[var(--color-bg-secondary)] p-4 transition-colors hover:bg-[var(--color-bg-tertiary)] [&>[data-text]]:contents'
              >
                <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--color-bg-tertiary)]'>
                  <Calendar className='size-5 text-[var(--color-text-secondary)]' />
                </div>
                <div>
                  <p className='text-sm font-medium text-[var(--color-text-primary)]'>
                    Need something more involved?
                  </p>
                  <p className='mt-0.5 text-xs text-[var(--color-text-tertiary)]'>
                    Book time with the team
                  </p>
                </div>
              </Button>
            </div>
          </div>
        </SlideoutMenu.Content>
      </SlideoutMenu>

      <CrmWebhookSecretModal
        isOpen={!!secretData}
        onClose={handleSecretModalClose}
        data={secretData}
      />
    </>
  );
}
